import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import AnalyticsSignalPanels from './AnalyticsSignalPanels';
import AnalyticsTimelineWorkspace from './AnalyticsTimelineWorkspace';
import VisitorDossierHeader from './VisitorDossierHeader';
import VisitorGroupsPanel from './VisitorGroupsPanel';
import {
  displayAnalyticsValue,
  formatTimestamp,
} from '../utils/analyticsDisplay';

const TOKEN_KEY = 'analytics_auth_token';
const EMPTY_ARRAY = [];
const INITIAL_VISITOR_FILTERS = {
  search: '',
  referrerHost: '',
  utmSource: '',
  utmMedium: '',
  landingPath: '',
  returningOnly: false,
};

const VIEW_COPY = {
  overview: {
    label: 'Overview',
    description: 'KPI row, signals, and audience mix.',
  },
  dossiers: {
    label: 'Dossiers',
    description: 'Visitor groups and intent drilldown.',
  },
  timeline: {
    label: 'Timeline',
    description: 'Chronological session and event tables.',
  },
};

const HistoricalAnalytics = ({ activeTab = 'overview' }) => {
  const [historicalData, setHistoricalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [visitorFilters, setVisitorFilters] = useState(INITIAL_VISITOR_FILTERS);

  useEffect(() => {
    void fetchHistoricalData();

    const interval = setInterval(() => {
      void fetchHistoricalData(true);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchHistoricalData = async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setError('You are not authenticated for analytics access.');
        setHistoricalData(null);
        return;
      }

      const response = await fetch('/api/analytics', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const body = await response.json().catch(() => ({}));

      if (response.status === 401) {
        setError('Your analytics session expired. Please log out and log in again.');
        setHistoricalData(null);
        return;
      }

      if (!response.ok) {
        const serverError = body?.error || `Request failed (${response.status})`;
        setError(serverError);
        setHistoricalData(null);
        return;
      }

      setHistoricalData(normalizePayload(body));
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load analytics data.');
      setHistoricalData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const summary = historicalData?.summary;
  const distributions = historicalData?.distributions;
  const visitors = historicalData?.visitors ?? EMPTY_ARRAY;
  const recentEvents = historicalData?.recentEvents ?? EMPTY_ARRAY;
  const deferredSearch = useDeferredValue(visitorFilters.search);
  const signals = {
    funnels: historicalData?.funnels || {},
    attribution: historicalData?.attribution || {},
    trafficQuality: historicalData?.trafficQuality || {},
    recentAssetRequests: historicalData?.recentAssetRequests || [],
  };
  const humanVisitors = Number(signals.trafficQuality?.humanVisitors || 0);
  const humanSessions = Number(signals.trafficQuality?.humanSessions || 0);
  const humanPageViews = Number(signals.trafficQuality?.humanPageViews || 0);
  const humanEvents = Number(signals.trafficQuality?.humanEvents || 0);

  const topCountries = useMemo(() => topEntries(distributions?.countries, 8), [distributions?.countries]);
  const topCities = useMemo(() => topEntries(distributions?.cities, 8), [distributions?.cities]);
  const topDevices = useMemo(() => topEntries(distributions?.devices, 8), [distributions?.devices]);
  const topBrowsers = useMemo(() => topEntries(distributions?.browsers, 8), [distributions?.browsers]);
  const topOs = useMemo(() => topEntries(distributions?.os, 8), [distributions?.os]);
  const topIsp = useMemo(() => topEntries(distributions?.isp, 8), [distributions?.isp]);
  const topAsn = useMemo(() => topEntries(distributions?.asn, 8), [distributions?.asn]);
  const topPaths = useMemo(() => topEntries(distributions?.paths, 8), [distributions?.paths]);

  const filteredVisitors = useMemo(
    () => filterVisitors(visitors, {
      search: deferredSearch,
      referrerHost: visitorFilters.referrerHost,
      utmSource: visitorFilters.utmSource,
      utmMedium: visitorFilters.utmMedium,
      landingPath: visitorFilters.landingPath,
      returningOnly: visitorFilters.returningOnly,
    }),
    [deferredSearch, visitors, visitorFilters.referrerHost, visitorFilters.utmSource, visitorFilters.utmMedium, visitorFilters.landingPath, visitorFilters.returningOnly]
  );

  const filteredRecentEvents = useMemo(
    () => normalizeRecentEvents(recentEvents, visitors),
    [recentEvents, visitors]
  );
  const hasVisitorFilters = hasActiveVisitorFilters(visitorFilters);

  if (loading && !historicalData) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="rounded-[30px] bg-white/85 p-8 shadow-[0_24px_80px_-50px_rgba(53,38,18,0.45)]">
          <div className="h-4 w-28 rounded-full bg-[#eadfce]"></div>
          <div className="mt-4 h-10 w-64 rounded-full bg-[#f0e6da]"></div>
          <div className="mt-3 h-4 w-96 max-w-full rounded-full bg-[#f5ede3]"></div>
        </div>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {[1, 2, 3, 4].map((value) => (
            <div key={value} className="h-36 rounded-[28px] bg-white/85 shadow-[0_24px_80px_-56px_rgba(53,38,18,0.45)]"></div>
          ))}
        </div>
      </div>
    );
  }

  const viewMeta = VIEW_COPY[activeTab] || VIEW_COPY.overview;

  return (
    <div className="space-y-6 lg:space-y-8">
      <section className="overflow-hidden rounded-[34px] border border-[#e6d9c7] bg-white/88 shadow-[0_34px_100px_-56px_rgba(42,30,12,0.42)]">
        <div
          className="border-b border-[#e8dccd] px-6 py-7 sm:px-8"
          style={{
            backgroundImage: 'linear-gradient(135deg, rgba(247,239,227,0.98) 0%, rgba(234,243,240,0.96) 54%, rgba(234,239,249,0.98) 100%)',
          }}
        >
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#8a6b42]">
                {viewMeta.label}
              </p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight text-[#1f1a14] sm:text-4xl">
                Internal analytics
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-[#5e5347] sm:text-base">
                {viewMeta.description}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {summary?.lastUpdated && (
                <span className="rounded-full border border-white/80 bg-white/72 px-4 py-2 text-sm font-medium text-[#5d5247]">
                  Last updated {formatTimestamp(summary.lastUpdated)}
                </span>
              )}
              <button
                type="button"
                onClick={() => void fetchHistoricalData(true)}
                disabled={refreshing}
                className="rounded-full bg-[#1f4f67] px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#184155] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {refreshing ? 'Refreshing...' : 'Refresh data'}
              </button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <MetaPill label="Visitors" value={humanVisitors} />
            <MetaPill label="Sessions" value={humanSessions} />
            <MetaPill label="Page views" value={humanPageViews} />
            <MetaPill label="Events" value={humanEvents} />
          </div>
        </div>

        {error && (
          <div className="border-t border-red-200 bg-red-50/90 px-6 py-4 sm:px-8">
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}
      </section>

      {!error && (
        <>
          {activeTab === 'overview' && (
            <section className="space-y-6">
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                <StatCard title="Visitors" value={humanVisitors} icon="👥" subtitle="Human" tone="sand" />
                <StatCard title="Sessions" value={humanSessions} icon="🧭" subtitle="Human" tone="mint" />
                <StatCard title="Page views" value={humanPageViews} icon="📄" subtitle="Human" tone="sky" />
                <StatCard title="Events" value={humanEvents} icon="⚙️" subtitle="Human" tone="lavender" />
              </div>

              <AnalyticsSignalPanels {...signals} />

              <section className="rounded-[32px] border border-[#e6d9c7] bg-white/88 p-6 shadow-[0_30px_90px_-58px_rgba(42,30,12,0.42)] sm:p-8">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#8a6b42]">Audience mix</p>
                    <h3 className="mt-2 text-2xl font-semibold text-[#1f1a14]">Geography, devices, and network mix</h3>
                  </div>
                  <span className="rounded-full border border-[#ebdecd] bg-[#f7f0e4] px-4 py-2 text-sm font-medium text-[#71583a]">
                    {Number(summary?.totalVisitors || 0).toLocaleString()} total visitors
                  </span>
                </div>

                <div className="mt-8 grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
                  <DistributionCard title="Countries" entries={topCountries} accent="green" />
                  <DistributionCard title="Cities" entries={topCities} accent="teal" />
                  <DistributionCard title="Devices" entries={topDevices} accent="blue" />
                  <DistributionCard title="Browsers" entries={topBrowsers} accent="purple" />
                  <DistributionCard title="Operating systems" entries={topOs} accent="indigo" />
                  <DistributionCard title="Pages" entries={topPaths} accent="amber" />
                  <DistributionCard title="ISP / Network" entries={topIsp} accent="rose" />
                  <DistributionCard title="ASN" entries={topAsn} accent="cyan" />
                </div>
              </section>
            </section>
          )}

          {activeTab === 'dossiers' && (
            <section className="space-y-6">
              <VisitorDossierHeader
                filters={visitorFilters}
                matchingCount={filteredVisitors.length}
                totalCount={visitors.length}
                recentEvents={filteredRecentEvents}
                onChange={(field, value) => {
                  setVisitorFilters((current) => ({
                    ...current,
                    [field]: value,
                  }));
                }}
                onReset={() => setVisitorFilters(INITIAL_VISITOR_FILTERS)}
              />

              <VisitorGroupsPanel visitors={filteredVisitors} hasFilters={hasVisitorFilters} />
            </section>
          )}

          {activeTab === 'timeline' && (
            <section className="space-y-6">
              <AnalyticsTimelineWorkspace
                visitors={visitors}
                recentEvents={recentEvents}
                recentAssetRequests={signals.recentAssetRequests}
                authToken={getStoredAuthToken()}
              />
            </section>
          )}
        </>
      )}
    </div>
  );
};

const normalizePayload = (payload) => ({
  summary: {
    totalVisitors: Number(payload?.summary?.totalVisitors || 0),
    totalSessions: Number(payload?.summary?.totalSessions || 0),
    totalPageViews: Number(payload?.summary?.totalPageViews || 0),
    totalEvents: Number(payload?.summary?.totalEvents || 0),
    lastUpdated: payload?.summary?.lastUpdated || null,
  },
  distributions: {
    countries: payload?.distributions?.countries || {},
    regions: payload?.distributions?.regions || {},
    cities: payload?.distributions?.cities || {},
    devices: payload?.distributions?.devices || {},
    browsers: payload?.distributions?.browsers || {},
    os: payload?.distributions?.os || {},
    isp: payload?.distributions?.isp || {},
    asn: payload?.distributions?.asn || {},
    paths: payload?.distributions?.paths || {},
    hourOfDayUtc: payload?.distributions?.hourOfDayUtc || {},
  },
  recentSessions: Array.isArray(payload?.recentSessions) ? payload.recentSessions : [],
  visitors: Array.isArray(payload?.visitors) ? payload.visitors : [],
  recentEvents: Array.isArray(payload?.recentEvents) ? payload.recentEvents : [],
  funnels: payload?.funnels || {},
  attribution: payload?.attribution || {},
  trafficQuality: payload?.trafficQuality || {},
  recentAssetRequests: Array.isArray(payload?.recentAssetRequests) ? payload.recentAssetRequests : [],
});

const filterVisitors = (visitors, filters) =>
  visitors.filter((visitor) => {
    const firstAttribution = resolveAttribution(visitor.firstAttribution || visitor.attribution);
    const latestAttribution = resolveAttribution(visitor.latestAttribution || visitor.attribution || firstAttribution);
    const latestSession = Array.isArray(visitor.recentSessions) && visitor.recentSessions.length > 0
      ? visitor.recentSessions[0]
      : null;
    const searchText = normalizeSearchText(filters.search);

    if (filters.returningOnly && Number(visitor.sessionCount || 0) <= 1) {
      return false;
    }

    if (!matchesFilter(filters.referrerHost, firstAttribution.referrerHost, latestAttribution.referrerHost)) {
      return false;
    }

    if (!matchesFilter(filters.utmSource, firstAttribution.utmSource, latestAttribution.utmSource)) {
      return false;
    }

    if (!matchesFilter(filters.utmMedium, firstAttribution.utmMedium, latestAttribution.utmMedium)) {
      return false;
    }

    if (!matchesFilter(filters.landingPath, firstAttribution.landingPath, latestAttribution.landingPath)) {
      return false;
    }

    if (!searchText) {
      return true;
    }

    const haystack = [
      visitor.userId,
      firstAttribution.referrerHost,
      latestAttribution.referrerHost,
      firstAttribution.utmSource,
      latestAttribution.utmSource,
      latestAttribution.utmMedium,
      latestSession?.lastPath,
      latestSession?.entryPath,
      firstAttribution.landingPath,
    ]
      .map(normalizeSearchText)
      .filter(Boolean)
      .join(' | ');

    return haystack.includes(searchText);
  });

const normalizeRecentEvents = (events, visitors) => {
  const sourceEvents = Array.isArray(events) && events.length > 0
    ? events
    : flattenVisitorActions(visitors);

  return [...sourceEvents]
    .map((event) => ({
      eventType: event?.eventType || '',
      ts: Number(event?.ts || 0),
      userId: event?.userId || event?.visitorId || '',
      sessionId: event?.sessionId || '',
      path: event?.path || '',
      details: event?.details || {},
      attribution: event?.attribution || null,
      trafficClass: event?.trafficClass || 'human',
    }))
    .filter((event) => Boolean(event.eventType))
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 8);
};

const flattenVisitorActions = (visitors) =>
  visitors.flatMap((visitor) =>
    Array.isArray(visitor?.recentActions)
      ? visitor.recentActions.map((action) => ({
        ...action,
        userId: action.userId || visitor.userId,
      }))
      : []
  );

const resolveAttribution = (attribution) => {
  const source = attribution && typeof attribution === 'object' ? attribution : {};
  return {
    landingPath: source.landingPath || '',
    referrerHost: source.referrerHost || '',
    utmSource: source.utmSource || '',
    utmMedium: source.utmMedium || '',
  };
};

const normalizeSearchText = (value) => displayAnalyticsValue(value, '').toLowerCase().trim();

const matchesFilter = (filterValue, ...candidates) => {
  const needle = normalizeSearchText(filterValue);
  if (!needle) {
    return true;
  }

  return candidates.some((candidate) => normalizeSearchText(candidate).includes(needle));
};

const hasActiveVisitorFilters = (filters) =>
  Boolean(
    normalizeSearchText(filters.search)
    || normalizeSearchText(filters.referrerHost)
    || normalizeSearchText(filters.utmSource)
    || normalizeSearchText(filters.utmMedium)
    || normalizeSearchText(filters.landingPath)
    || filters.returningOnly
  );

const topEntries = (data = {}, max = 8) =>
  Object.entries(data)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, max);

const MetaPill = ({ label, value }) => (
  <div className="rounded-full border border-[#eadfce] bg-[#f8f2e8] px-4 py-2 text-sm text-[#6d5a43]">
    <span className="font-semibold text-[#2a231b]">{Number(value || 0).toLocaleString()}</span> {label}
  </div>
);

const DistributionCard = ({ title, entries, accent = 'blue' }) => {
  const accentClass = {
    green: 'bg-[#edf8f3] text-[#2d6a55]',
    teal: 'bg-[#e7f5f3] text-[#1f6a66]',
    blue: 'bg-[#ecf1fb] text-[#385798]',
    purple: 'bg-[#f1ecfb] text-[#6c4ea4]',
    indigo: 'bg-[#e8ecfb] text-[#4857a0]',
    amber: 'bg-[#faf0df] text-[#996729]',
    rose: 'bg-[#f9ecec] text-[#96545a]',
    cyan: 'bg-[#e6f6fa] text-[#236d83]',
  }[accent] || 'bg-[#ecf1fb] text-[#385798]';

  return (
    <div className="rounded-[28px] border border-[#e8dccd] bg-white/88 p-6 shadow-[0_24px_80px_-60px_rgba(42,30,12,0.42)]">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-lg font-semibold text-[#1f1a14]">{title}</h4>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${accentClass}`}>
          Top 8
        </span>
      </div>

      {entries.length === 0 ? (
        <p className="mt-6 text-sm text-[#71665b]">No data</p>
      ) : (
        <div className="mt-6 space-y-3">
          {entries.map(([label, count], index) => {
            const displayLabel = displayAnalyticsValue(label, 'Unknown');
            return (
              <div key={`${label}-${index}`} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
                <span className="truncate text-sm font-medium text-[#372f28]" title={displayLabel}>
                  {displayLabel}
                </span>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${accentClass}`}>
                  {Number(count).toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon, subtitle, tone }) => {
  const palette = {
    sand: 'from-[#f7efe1] to-[#fbf7f1] border-[#e9dac1]',
    mint: 'from-[#ebf7f2] to-[#f7fcfa] border-[#d7ebe3]',
    sky: 'from-[#edf2fb] to-[#f8faff] border-[#d9e2f3]',
    lavender: 'from-[#f2eefb] to-[#faf8ff] border-[#e4daf7]',
  }[tone] || 'from-[#f7efe1] to-[#fbf7f1] border-[#e9dac1]';

  return (
    <div className={`overflow-hidden rounded-[30px] border bg-gradient-to-br p-6 shadow-[0_24px_80px_-56px_rgba(42,30,12,0.4)] ${palette}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#8c7a66]">{title}</p>
          <p className="mt-3 text-4xl font-semibold tracking-tight text-[#201a14]">
            {Number(value || 0).toLocaleString()}
          </p>
          <p className="mt-2 text-sm text-[#675d52]">{subtitle}</p>
        </div>
        <div className="rounded-full bg-white/70 px-4 py-3 text-2xl shadow-sm">{icon}</div>
      </div>
    </div>
  );
};

const getStoredAuthToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
};

export default HistoricalAnalytics;
